import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

/**
 * Public cash-close form · Daily Recon M2.
 *
 * The shift manager taps the link on their phone. No login. The URL
 * carries a token that resolves to a specific client server-side.
 *
 * Design intent: this looks like a form, not a dashboard. Numeric
 * keypad on every field via `inputMode="decimal"`. Expected close is
 * derived live so the manager sees the variance BEFORE they submit —
 * a small honesty affordance so the till gets balanced now, not
 * disputed next week.
 */
const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n || 0);

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

export default function CashClosePublic() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const [form, setForm] = useState({
    business_date: yesterday(),
    opening_float: '',
    cash_sales: '',
    cash_deposits: '',
    closing_float: '',
    manager_name: '',
    manager_phone: '',
    notes: '',
  });

  useEffect(() => {
    axios
      .get(`${API}/api/public/cash-close/${token}`)
      .then((r) => setCtx(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Link is not valid.'));
  }, [token]);

  const expected = useMemo(() => {
    const o = parseFloat(form.opening_float) || 0;
    const s = parseFloat(form.cash_sales) || 0;
    const d = parseFloat(form.cash_deposits) || 0;
    return o + s - d;
  }, [form.opening_float, form.cash_sales, form.cash_deposits]);

  const variance = useMemo(() => {
    const c = parseFloat(form.closing_float);
    if (isNaN(c)) return null;
    return c - expected;
  }, [form.closing_float, expected]);

  const canSubmit =
    form.business_date &&
    form.opening_float !== '' &&
    form.cash_sales !== '' &&
    form.cash_deposits !== '' &&
    form.closing_float !== '' &&
    form.manager_name.trim().length > 0 &&
    !submitting;

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      const { data } = await axios.post(`${API}/api/public/cash-close/${token}`, {
        ...form,
        opening_float: parseFloat(form.opening_float),
        cash_sales: parseFloat(form.cash_sales),
        cash_deposits: parseFloat(form.cash_deposits),
        closing_float: parseFloat(form.closing_float),
      });
      setReceipt(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (err && !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0d0d10] text-white">
        <div className="max-w-sm text-center" data-testid="cash-close-error">
          <div className="text-3xl mb-4">🔒</div>
          <p className="text-lg">{err}</p>
        </div>
      </div>
    );
  }
  if (!ctx) return null;
  if (receipt) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0d0d10] text-white">
        <div className="max-w-sm w-full space-y-5 text-center" data-testid="cash-close-receipt">
          <div className="text-4xl">✓</div>
          <h1 className="font-serif text-2xl">
            {receipt.is_revision ? 'Revision recorded' : 'Cash close received'}
          </h1>
          <p className="text-white/70 text-sm">{receipt.message}</p>
          <div className="mt-6 bg-white/5 rounded-lg p-4 space-y-2 text-left">
            <Row label="Business date" v={receipt.business_date} />
            <Row label="Expected close" v={'₹' + fmt(receipt.expected_close)} />
            <Row label="Counted close" v={'₹' + fmt(receipt.closing_float)} />
            <Row
              label="Variance"
              v={(receipt.variance >= 0 ? '+' : '') + '₹' + fmt(receipt.variance)}
              tone={
                receipt.variance === 0
                  ? 'text-emerald-300'
                  : Math.abs(receipt.variance) < 100
                  ? 'text-amber-300'
                  : 'text-rose-300'
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d10] text-white px-5 py-8">
      <div className="max-w-md mx-auto space-y-6">
        <header>
          <div className="text-xs uppercase tracking-widest text-white/45">
            {ctx.client_name} · Cash close
          </div>
          <h1 className="font-serif text-3xl mt-1">Close today&apos;s till.</h1>
          <p className="text-white/60 text-sm mt-2">
            Every rupee in the till. Every rupee to the bank. Difference — if any —
            is what we&apos;ll help explain tomorrow.
          </p>
        </header>

        <Field label="Business date" hint="The day this close covers">
          <input
            type="date"
            value={form.business_date}
            data-testid="cc-business-date"
            onChange={(e) => setForm({ ...form, business_date: e.target.value })}
            className="w-full bg-white/5 rounded-lg px-4 py-3 text-white outline-none"
          />
        </Field>

        <Field label="Opening float" hint="₹ in the till at shift start">
          <MoneyInput
            v={form.opening_float}
            testid="cc-opening-float"
            onV={(v) => setForm({ ...form, opening_float: v })}
          />
        </Field>

        <Field label="Cash sales" hint="₹ taken as cash from customers">
          <MoneyInput
            v={form.cash_sales}
            testid="cc-cash-sales"
            onV={(v) => setForm({ ...form, cash_sales: v })}
          />
        </Field>

        <Field label="Cash deposits" hint="₹ moved to the bank / owner">
          <MoneyInput
            v={form.cash_deposits}
            testid="cc-cash-deposits"
            onV={(v) => setForm({ ...form, cash_deposits: v })}
          />
        </Field>

        <div className="bg-white/5 rounded-lg p-4 -my-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Expected close</span>
            <span className="font-serif text-xl">₹{fmt(expected)}</span>
          </div>
          <div className="text-xs text-white/40 mt-1">
            = opening + cash sales − deposits
          </div>
        </div>

        <Field label="Closing float" hint="₹ actually counted in the till at close">
          <MoneyInput
            v={form.closing_float}
            testid="cc-closing-float"
            onV={(v) => setForm({ ...form, closing_float: v })}
          />
        </Field>

        {variance !== null && (
          <div className="text-sm">
            <span className="text-white/60">Variance: </span>
            <span
              className={
                variance === 0
                  ? 'text-emerald-300'
                  : Math.abs(variance) < 100
                  ? 'text-amber-300'
                  : 'text-rose-300'
              }
              data-testid="cc-variance"
            >
              {variance >= 0 ? '+' : ''}₹{fmt(variance)}
            </span>
          </div>
        )}

        <Field label="Your name">
          <input
            value={form.manager_name}
            data-testid="cc-manager-name"
            onChange={(e) => setForm({ ...form, manager_name: e.target.value })}
            placeholder="Shift manager"
            className="w-full bg-white/5 rounded-lg px-4 py-3 text-white outline-none"
          />
        </Field>

        <Field label="Phone (optional)">
          <input
            value={form.manager_phone}
            inputMode="tel"
            data-testid="cc-manager-phone"
            onChange={(e) => setForm({ ...form, manager_phone: e.target.value })}
            placeholder="+91 …"
            className="w-full bg-white/5 rounded-lg px-4 py-3 text-white outline-none"
          />
        </Field>

        <Field label="Notes (optional)">
          <textarea
            value={form.notes}
            data-testid="cc-notes"
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            placeholder="Anything unusual — refund, tip payout, drawer short"
            className="w-full bg-white/5 rounded-lg px-4 py-3 text-white outline-none resize-none"
          />
        </Field>

        {err && (
          <div className="text-sm text-rose-300" data-testid="cc-submit-error">
            {err}
          </div>
        )}

        <button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="cc-submit-btn"
          className="w-full py-4 rounded-lg bg-[#C9A66B] text-black font-medium disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? 'Submitting…' : 'Submit cash close'}
        </button>
        <div className="text-center text-xs text-white/30 pt-4">
          Epicurea Nexus · Every rupee accounted for.
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-white/50">{label}</label>
    {hint && <div className="text-xs text-white/35 mb-2 mt-0.5">{hint}</div>}
    {!hint && <div className="mb-2" />}
    {children}
  </div>
);

const MoneyInput = ({ v, onV, testid }) => (
  <div className="flex items-center bg-white/5 rounded-lg px-4">
    <span className="text-white/40 text-lg mr-2">₹</span>
    <input
      inputMode="decimal"
      value={v}
      data-testid={testid}
      onChange={(e) => onV(e.target.value.replace(/[^0-9.]/g, ''))}
      placeholder="0"
      className="flex-1 bg-transparent py-3 text-white text-lg outline-none"
    />
  </div>
);

const Row = ({ label, v, tone }) => (
  <div className="flex justify-between text-sm">
    <span className="text-white/60">{label}</span>
    <span className={tone || 'text-white'}>{v}</span>
  </div>
);
